import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Divider, FormControl, IconButton, Stack, TextField, Typography, SvgIcon, Drawer } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddNewQuestionDrawer from './AddNewQuestionDrawer';

/**
 * @description
 * This component is used to render the screening questions input form when creating a job posting.
 * It takes in the questions, setQuestions, errors, setErrors, handlePostJob, showInitialScreen, and setShowInitialScreen as props
 * and renders the form with the input fields and buttons to add, edit, and delete questions.
 * When the user clicks on the "Add Next Question" button, it will add a new question to the questions array.
 * When the user clicks on the "Edit" button on a question, it will open the AddNewQuestionDrawer component
 * and pass the question index and the question itself as props. When the user clicks on the "Delete" button on a question,
 * it will remove the question from the questions array. When the user clicks on the "Post Job" button, it will call the handlePostJob function.
 * @param questions - The array of questions
 * @param setQuestions - The function to set the questions array
 * @param errors - The array of errors
 * @param setErrors - The function to set the errors array
 * @param handlePostJob - The function to call when the user clicks on the "Post Job" button
 * @param showInitialScreen - The boolean to show the initial screen
 * @param setShowInitialScreen - The function to set the showInitialScreen boolean
 * @returns The JSX element for the screening questions input form
 */
const ScreeningQuestionList = ({ questions, setQuestions, errors, setErrors, handlePostJob, showInitialScreen, setShowInitialScreen, alignment }) => {

  const questionRefs = useRef([]);
  const [posting, setPosting] = useState(false);
  const [limitError, setLimitError] = useState(false);
  const [openAddnewQue, setOpenAddNewQueDrawer] = useState(false);
  const [editExternalQus, setEditExternalQus] = useState(null);
  const [editExternalQusIndex, setEditExternalQusIndex] = useState(null);

  const defaultQuestion = 'What skills, qualifications and accomplishments make you a good fit for this role?';

  useEffect(() => {
    if (questionRefs?.current[0]) {
      questionRefs?.current[0]?.focus();
    }
  }, []);

  useEffect(() => {
    if (!questions.includes(defaultQuestion)) {
      setQuestions([defaultQuestion, ...questions]);
    }
    const filterQuestion = (questions?.filter(q => q !== '') || []);
    if (filterQuestion?.length > 0) {
      setShowInitialScreen(false);
    } else {
      setShowInitialScreen(true);
    }
  }, []);

  /**
   * Updates the question at the specified index with the new value from the input event.
   * @param {number} index - The index of the question to update.
   * @param {object} event - The event object containing the new value for the question.
   */

  const handleQuestionChange = (index, event) => {
    const newQuestions = [...questions];
    newQuestions[index] = event.target.value;
    setQuestions(newQuestions);
  };

  /**
   * Adds a new question to the list of questions when the user clicks the "Add Question" button.
   * If the user has already added 5 questions, this function will not do anything.
   * @returns {void}
   */
  const handleAddQuestion = () => {
    if (questions?.length >= 5) {
      setLimitError(true);
      return
    }
    setLimitError(false);
    setQuestions([...questions, '']);
    setTimeout(() => {
      const newIndex = questions?.length;
      questionRefs?.current[newIndex]?.focus();
    }, 0);
  };

  /**
   * Removes the question at the specified index from the list of questions.
   * It also removes the corresponding error at the same index from the errors array.
   * @param {number} index - The index of the question to remove.
   * @returns {void}
   */
  const handleRemoveQuestion = (index) => {
    // console.log(index);
    const newQuestions = questions.filter((_, i) => i !== index);
    setQuestions(newQuestions);
    const newErrors = errors.filter((_, i) => i !== index);
    setErrors(newErrors);
  };

  /**
   * Sets the posting state to true and calls the handlePostJob function
   * to initiate the job posting process.
   * @returns {void}
   */

  const postingJob = () => {
    setPosting(true);
    handlePostJob();
  };

  /**
   * Sets the open state of the add new question drawer to the provided value
   * and resets the editExternalQus and editExternalQusIndex states to null.
   * This function is used to open and close the add new question drawer.
   * @param {boolean} newOpen - The new open state of the drawer.
   * @returns {function} A function to set the open state of the drawer.
   */
  const addQueDrawer = (newOpen) => () => {
    setOpenAddNewQueDrawer(newOpen);
    setEditExternalQus(null);
    setEditExternalQusIndex(null);
  };
  /**
   * Opens the add new question drawer and sets the editExternalQus and editExternalQusIndex states
  
   * to the provided qus and index values. This function is used to allow the user to edit an
   * existing external question.
   * @param {string} qus - The question to edit.
   * @param {number} index - The index of the question to edit.
   * @returns {void}
   */
  const handleAddExternalQuestion = (qus, index) => {
    setOpenAddNewQueDrawer(true);
    setEditExternalQus(qus);
    setEditExternalQusIndex(index);
  };
  /**
   * Deletes an external question from the questions list based on the provided index.
   * Updates the state with the new list of questions after removal.
   * 
   * @param {string} qus - The question to be deleted (not used in the function).
   * @param {number} index - The index of the question to delete.
   * @returns {void}
   */

  const handleDeleteExternalQuestion = (qus, index) => {
    const newQuestions = questions.filter((_, i) => i !== index);
    setQuestions(newQuestions);
  };

  return (
    <Box className="screening-que-container">
      {/* index section screen  */} {/* questions list scren  */}
      {showInitialScreen ?
        <Box className="screening-que-index">
          <Stack spacing={2}>
            <Typography className="title">Add screening questions</Typography>
            <Typography className="info-text">
              Screening questions help in cutting the noise & make sure that only appropriate candidature would be able to get through the job application process
            </Typography>
            <Button fullWidth className="theme-contained-btn" variant="contained" size="large" onClick={() => { setShowInitialScreen(false) }}>
              Add Screening Questions
            </Button>
            <Button className="skip-btn" size="medium" disabled={posting} onClick={postingJob}>
              {posting ? 'Posting...' : 'Skip This & Post Job'}
            </Button>
          </Stack>
        </Box> :
        <Box className="screening-que-list" sx={{ paddingBottom: '10vh' }}>
          {alignment === 'tj-jobs' ?
            <Box className="screeing-que-internal" >
              {/* internal job */}
              <Box className="job-posting-que-internal" mb={2.5}>
                <Box mb={2} >
                  <Stack spacing={2}>
                    <Box>
                      <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                        <Typography className="que-text">Cover note</Typography>
                        <Box className="non-edit">non-editable</Box>
                      </Stack>
                    </Box>
                    <FormControl fullWidth>
                      <TextField
                        label="Question 1"
                        size="small"
                        variant="outlined"
                        value={defaultQuestion}
                        multiline
                        InputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </FormControl>
                  </Stack>

                  <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginTop: '1.5rem', marginBottom: '1rem' }} />
                </Box>
                {questions?.filter((question) => question != defaultQuestion)?.map((question, index) => (
                  <>
                    <Stack spacing={2} key={index}>
                      <Box>
                        <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                          <Typography className="que-text">Question {index + 2}</Typography>
                          <IconButton className="delete-icon-btn" aria-label="delete" onClick={() => handleRemoveQuestion(index + 1)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M11.0948 2.37734C11.0948 2.22733 11.0353 2.08301 10.9289 1.97715C10.8231 1.87074 10.6787 1.81131 10.5287 1.81131H8.70671L8.44689 0.771503C8.33369 0.317545 7.92557 -0.000564375 7.45803 7.54076e-07H4.54298C4.07543 -0.000565244 3.66732 0.317545 3.55412 0.771503L3.29431 1.81131H1.47228C1.15983 1.81131 0.90625 2.06489 0.90625 2.37734C0.90625 2.68978 1.15983 2.94337 1.47228 2.94337H1.50171C1.46096 3.10186 1.44737 3.26658 1.46096 3.42959L2.14473 10.2668C2.24096 11.2523 3.0702 12.0027 4.06023 12H7.94092C8.93091 12.0028 9.7602 11.2523 9.85642 10.2668L10.5402 3.42901C10.5538 3.266 10.5396 3.10185 10.4994 2.94335H10.5289C10.6789 2.94335 10.8232 2.88392 10.9291 2.77751C11.0355 2.67166 11.0948 2.52733 11.0948 2.37734ZM4.63071 1.13206H7.3697L7.53951 1.8113H4.46087L4.63071 1.13206ZM9.32707 3.05494C9.3916 3.1257 9.4233 3.22135 9.41311 3.31702L8.72933 10.1542C8.69028 10.5601 8.34839 10.8691 7.94085 10.868H4.06015C3.65261 10.8691 3.31072 10.5601 3.27167 10.1542L2.58789 3.3176C2.57827 3.22194 2.6094 3.12684 2.67393 3.05552C2.73846 2.9842 2.82959 2.94345 2.92582 2.94345H9.07529C9.17151 2.94288 9.26255 2.98362 9.32707 3.05494ZM5.09484 4.52832C4.94483 4.52832 4.80051 4.58775 4.69465 4.69416C4.58824 4.80001 4.52881 4.94435 4.52881 5.09434V7.81131C4.52881 8.12375 4.78239 8.37734 5.09484 8.37734C5.40728 8.37734 5.66086 8.12375 5.66086 7.81131V5.09434C5.66086 4.94434 5.60143 4.80001 5.49502 4.69416C5.38917 4.58774 5.24483 4.52832 5.09484 4.52832ZM6.50599 4.69416C6.61185 4.58775 6.75617 4.52832 6.90618 4.52832C7.05617 4.52832 7.20051 4.58774 7.30636 4.69416C7.41277 4.80001 7.4722 4.94434 7.4722 5.09434V7.81131C7.4722 8.12375 7.21862 8.37734 6.90618 8.37734C6.59373 8.37734 6.34015 8.12375 6.34015 7.81131V5.09434C6.34015 4.94435 6.39958 4.80001 6.50599 4.69416Z"
                                fill="#FF4949"
                              />
                            </svg>
                          </IconButton>
                        </Stack>
                      </Box>
                      <FormControl fullWidth>
                        <TextField
                          label="Your Question"
                          size="small"
                          variant="outlined"
                          value={question}
                          onChange={(e) => handleQuestionChange(index + 1, e)}
                          error={errors[index + 1]}
                          helperText={errors[index + 1] ? 'This field cannot be empty' : ''}
                          inputRef={(el) => (questionRefs.current[index] = el)}
                          multiline
                        />
                      </FormControl>
                    </Stack>
                    <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginTop: '1.5rem', marginBottom: '1rem' }} />
                  </>))}
              </Box>
              <Button className="next-que-btn" size="medium" onClick={handleAddQuestion} disabled={questions.length >= 5 || limitError}>
                Add Next Question
              </Button>
              {questions?.length >= 5 && (
                <Typography color="error" sx={{ marginTop: '1rem' }}>
                  You can only add up to 5 questions
                </Typography>
              )}
            </Box> :
            <Box className="screeing-que-external" >
              {/* external job */}
              <Box mb={2.5}>
                <Stack spacing={1}>
                  <Typography className="job-type-text">{defaultQuestion}</Typography>
                  <Box className="non-edit">non-editable</Box>
                </Stack>
              </Box>
              <Divider sx={{ marginBottom: 2.5 }} />

              {questions?.filter((question) => question != defaultQuestion)?.map((question, index) => (
                <>
                  <Box mb={2.5} key={index}>
                    <Stack spacing={1}>
                      <Typography className="job-type-text">{question}</Typography>
                      <Stack direction="row" alignItems={'center'} spacing={1.2}>
                        <IconButton className="edit-delete-icon edit" aria-label="delete" onClick={() => handleAddExternalQuestion(question, index + 1)}>
                          <SvgIcon>
                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path d="M7.57324 9.80049H1.45966C0.765598 9.80049 0.200928 9.23588 0.200928 8.54175V2.42817C0.200928 1.7341 0.765529 1.16943 1.45966 1.16943H4.26115C4.45985 1.16943 4.62077 1.33036 4.62077 1.52906C4.62077 1.72775 4.45985 1.88868 4.26115 1.88868H1.45966C1.16208 1.88868 0.92023 2.13053 0.92023 2.42811V8.54169C0.92023 8.83927 1.16208 9.08113 1.45966 9.08113H7.57324C7.87083 9.08113 8.11268 8.83927 8.11268 8.54169V5.7402C8.11268 5.54151 8.2736 5.38058 8.4723 5.38058C8.67099 5.38058 8.83192 5.54151 8.83192 5.7402V8.54169C8.83192 9.23576 8.26738 9.80049 7.57324 9.80049Z" fill="#1F9CD7" />
                              <path d="M2.86523 5.60522L6.79956 1.6709L8.32747 3.1988L4.39314 7.13313L2.86523 5.60522Z" fill="#1F9CD7" />
                              <path d="M9.62591 1.06396L8.93543 0.373487C8.70438 0.142431 8.32947 0.142431 8.09797 0.373487L7.30859 1.16286L8.8365 2.69077L9.62587 1.90139C9.85693 1.67034 9.85696 1.29547 9.62591 1.06396Z" fill="#1F9CD7" />
                              <path d="M2.40413 6.16113L2.04856 7.71378C2.01619 7.85583 2.14296 7.98259 2.28501 7.95023L3.83766 7.59466L2.40413 6.16113Z" fill="#1F9CD7" />
                            </svg>
                          </SvgIcon>
                        </IconButton>
                        <IconButton className="edit-delete-icon delete" aria-label="delete" onClick={() => handleDeleteExternalQuestion(question, index + 1)}>
                          <SvgIcon>
                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path fill-rule="evenodd" clip-rule="evenodd" d="M9.24531 1.98111C9.24531 1.85611 9.19579 1.73584 9.10711 1.64763C9.0189 1.55895 8.89862 1.50943 8.77363 1.50943H7.25526L7.03875 0.642919C6.94441 0.264621 6.60432 -0.000470312 6.2147 6.28397e-07H3.78549C3.39587 -0.000471037 3.05578 0.264621 2.96145 0.642919L2.74493 1.50943H1.22657C0.966202 1.50943 0.754883 1.72075 0.754883 1.98111C0.754883 2.24148 0.966202 2.4528 1.22657 2.4528H1.2511C1.21714 2.58488 1.20582 2.72215 1.21714 2.85799L1.78695 8.55565C1.86714 9.37687 2.55817 10.0023 3.3832 9.99999H6.61711C7.4421 10.0023 8.13318 9.37687 8.21336 8.55565L8.78317 2.85751C8.79449 2.72166 8.7827 2.58487 8.74921 2.45279H8.77374C8.89874 2.45279 9.01901 2.40326 9.10723 2.31459C9.1959 2.22638 9.24531 2.10611 9.24531 1.98111ZM3.8586 0.943379H6.14109L6.2826 1.50941H3.71706L3.8586 0.943379ZM7.77224 2.54578C7.82601 2.60475 7.85242 2.68446 7.84393 2.76418L7.27412 8.46184C7.24157 8.80004 6.95667 9.05759 6.61705 9.05665H3.38314C3.04352 9.05759 2.75861 8.80004 2.72607 8.46184L2.15625 2.76466C2.14823 2.68495 2.17418 2.6057 2.22795 2.54627C2.28172 2.48683 2.35766 2.45287 2.43786 2.45287H7.56241C7.6426 2.4524 7.71846 2.48635 7.77224 2.54578ZM4.24538 3.77351C4.12038 3.77351 4.00011 3.82304 3.9119 3.91171C3.82322 3.99992 3.77369 4.12021 3.77369 4.2452V6.50934C3.77369 6.76971 3.98501 6.98103 4.24538 6.98103C4.50575 6.98103 4.71707 6.76971 4.71707 6.50934V4.2452C4.71707 4.12019 4.66754 3.99992 4.57887 3.91171C4.49066 3.82303 4.37038 3.77351 4.24538 3.77351ZM5.42144 3.91171C5.50965 3.82304 5.62992 3.77351 5.75493 3.77351C5.87992 3.77351 6.00021 3.82303 6.08841 3.91171C6.17709 3.99992 6.22662 4.12019 6.22662 4.2452V6.50934C6.22662 6.76971 6.0153 6.98103 5.75493 6.98103C5.49456 6.98103 5.28324 6.76971 5.28324 6.50934V4.2452C5.28324 4.12021 5.33276 3.99992 5.42144 3.91171Z" fill="#FF4949" />
                            </svg>
                          </SvgIcon>
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                  <Divider sx={{ marginBottom: 2.5 }} />
                </>
              ))}

              <Box className="job-posting-que-external">
                <Stack spacing={2.5}>
                  <Typography className="ext-que-title">You can add up to {6 - questions?.length} more questions</Typography>
                  <Stack spacing={2.5}>
                    {Array.from({ length: 6 - (questions?.length || 0) }).map((_, index) => (
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={addQueDrawer(true)} disableRipple disableElevation>Question</Button>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Box>
          }
        </Box>
      }

      {/* Add new external question drawer */}
      <Drawer open={openAddnewQue} anchor={'right'} onClose={addQueDrawer(false)}>
        <AddNewQuestionDrawer questions={questions} setQuestions={setQuestions} onClose={addQueDrawer(false)} qusIndex={editExternalQusIndex} qus={editExternalQus} />
      </Drawer>
    </Box>
  );
};

export default ScreeningQuestionList;
